import React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { useLocale } from 'locale';
import Modal from 'ds/components/modal';
import { H3, Text } from 'ds/components/typography';
import SuccessSignIcon from 'assets/svg/private-bulletin-promotion/modal-success-sign.svg';
import InfoSignIcon from 'assets/svg/private-bulletin-promotion/modal-info-sign.svg';
import { State } from 'store/state';
import { routeSelector } from 'store/state/selectors/router';
import * as actions from 'store/state/router/actions';
import { State as RouteState, NavigateToFn, WithPaymentRouteParams } from 'config/routes';
import { Button } from 'ds/components/button';

const ContentRoot = styled.div(() => ({
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '420px',
}));

const Title = styled(H3)<{ isFailed: boolean }>(({ theme, isFailed }) => ({
  textAlign: 'center',
  fontWeight: 700,
  marginTop: theme.spacing(3),
  color: isFailed ? theme.colors.alert.red : theme.colors.alert.green,
}));

const DetailsSection = styled.div(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const BtnWrapper = styled.div(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));

interface PaymentResultModalProps {
  route: RouteState;
  navigateTo: NavigateToFn;
}

const PaymentResultModal: React.FC<PaymentResultModalProps> = props => {
  const { route, navigateTo } = props;
  const { t } = useLocale();
  const isOpen = route.params.payment_modal === 'true';
  const paymentOutcome = route.params.payment_outcome;
  const paymentId = route.params.payment_id;
  const responseCode = route.params.payment_response_code;
  const productType = route.params.payment_product_type;

  const handleClose = () => {
    const paramsToClear: Required<WithPaymentRouteParams> = {
      payment_modal: undefined,
      payment_id: undefined,
      payment_outcome: undefined,
      payment_response_code: undefined,
      payment_product_type: undefined,
      uuk: undefined,
      payment_source: undefined,
      payment_promotion_plan: undefined,
      payment_deal_type: undefined,
    };
    navigateTo(route.name, { ...route.params, ...paramsToClear }, { replace: true });
  };

  if (!isOpen || !paymentOutcome) return null;

  const isSuccess = paymentOutcome === 'success';
  const isFailed = paymentOutcome === 'failed';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      mobileModalPositionBottom
    >
      <ContentRoot>
        {isSuccess
          ? <SuccessSignIcon width="60" height="60" />
          : <InfoSignIcon width="80" height="80" />}
        <Title isFailed={isFailed}>
          {t('paymentResultModal.title', { outcome: paymentOutcome })}
        </Title>
        <DetailsSection>
          {isSuccess && productType === 'PROMOTION_PLAN' && (
            <Text style={{ marginBottom: '8px' }}>
              {t('paymentResultModal.promotionPlanWillBeActivatedSoon')}
            </Text>
          )}
          {isFailed && <Text weight="medium">{t('paymentResultModal.paymentId', { paymentId })}</Text>}
          {isFailed && <Text weight="medium">{t('paymentResultModal.responseCode', { responseCode })}</Text>}
          <BtnWrapper>
            <Button size="large" fullWidth onClick={handleClose}>{t('listingsPage.changeAgentModal.button.confirm')}</Button>
          </BtnWrapper>
        </DetailsSection>
      </ContentRoot>
    </Modal>
  );
};


const mapStateToProps = (state: State) => ({
  route: routeSelector(state),
});

const mapDispatchToProps = {
  navigateTo: actions.navigateTo,
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentResultModal);
